/**
 * Projects Gantt Chart
 *
 * Cross-project roadmap view showing spaces as rows on a timeline.
 * Bars represent the date range of non-completed tasks within each space.
 * Completion rate is visualized as a progress fill inside each bar.
 */

import { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  addMonths,
  subMonths,
  isToday,
  isSameMonth,
  differenceInDays,
  startOfDay,
} from 'date-fns';
import { it } from 'date-fns/locale';
import { ChevronLeft, ChevronRight, Calendar, Diamond } from 'lucide-react';
import clsx from 'clsx';

import type { Task } from '@/types';
import { supabase } from '@/lib/supabaseClient';
import {
  completeMilestone,
  type MilestoneWithProgress,
  type MilestoneStatus,
} from '@/db/milestones';
import { isCompletedStatus } from '@/constants/taskStatuses';
import { MilestonesPanel } from './MilestonesPanel';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SpaceInfo {
  id: string;
  name: string;
  taskCount: number;
  completedTasks: number;
  completionRate: number;
  overdueTasks: number;
}

export interface ProjectsGanttProps {
  spaces: SpaceInfo[];
  tasks: Task[];
  onSpaceNavigate: (spaceName: string) => void;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

type ZoomLevel = 'month' | 'quarter' | 'semester' | 'year';

const ROW_HEIGHT = 36;
const BAR_HEIGHT = 20;
const LEFT_PANEL_WIDTH = 200;
const DIAMOND_SIZE = 10;

function getDayWidth(zoom: ZoomLevel): number {
  switch (zoom) {
    case 'month': return 24;
    case 'quarter': return 8;
    case 'semester': return 4;
    case 'year': return 2;
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Build a map of spaceId -> { earliest, latest } from non-completed tasks */
function computeSpaceDateRanges(
  spaces: SpaceInfo[],
  tasks: Task[],
) {
  const map = new Map<
    string,
    { earliest: number; latest: number }
  >();

  for (const task of tasks) {
    const spaceId = task.spaceId;
    if (!spaceId) continue;

    // Use startDate, dueDate, or dateCreated as fallback for range
    const start = task.startDate ?? task.dueDate ?? task.dateCreated;
    const end = task.dueDate ?? task.startDate ?? task.dateCreated;
    if (!start && !end) continue;

    const entry = map.get(spaceId) ?? {
      earliest: Infinity,
      latest: -Infinity,
    };

    if (start) entry.earliest = Math.min(entry.earliest, start);
    if (end) entry.latest = Math.max(entry.latest, end);

    map.set(spaceId, entry);
  }

  return map;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ProjectsGantt({ spaces, tasks, onSpaceNavigate }: ProjectsGanttProps) {
  const [zoomLevel, setZoomLevel] = useState<ZoomLevel>('month');
  const [currentDate, setCurrentDate] = useState(() => new Date());
  const [hoveredSpaceId, setHoveredSpaceId] = useState<string | null>(null);
  const [tooltipInfo, setTooltipInfo] = useState<{
    spaceId: string;
    x: number;
    y: number;
  } | null>(null);
  const [milestonesPanelOpen, setMilestonesPanelOpen] = useState(false);
  const [milestones, setMilestones] = useState<MilestoneWithProgress[]>([]);
  const [milestoneTooltip, setMilestoneTooltip] = useState<{
    milestone: MilestoneWithProgress;
    x: number;
    y: number;
  } | null>(null);

  const timelineRef = useRef<HTMLDivElement>(null);
  const leftPanelRef = useRef<HTMLDivElement>(null);
  const milestoneBtnRef = useRef<HTMLDivElement>(null);

  // Outside click handling is done by MilestonesPanel via portal

  // Track container width to make dayWidth responsive
  const [containerWidth, setContainerWidth] = useState(1200);
  useEffect(() => {
    const el = timelineRef.current;
    if (!el) return;
    const obs = new ResizeObserver(([entry]) => setContainerWidth(entry.contentRect.width));
    obs.observe(el);
    setContainerWidth(el.clientWidth);
    return () => obs.disconnect();
  }, []);

  // ---- Date range — fixed period, dayWidth stretches to fill space ----
  const { dateRange, totalWidth, responsiveDayWidth } = useMemo(() => {
    const totalMonths = zoomLevel === 'month' ? 1 : zoomLevel === 'quarter' ? 3 : zoomLevel === 'semester' ? 6 : 12;
    const start = startOfMonth(currentDate);
    const end = endOfMonth(addMonths(currentDate, totalMonths - 1));
    const days = eachDayOfInterval({ start, end });
    // dayWidth stretches to fill container, but has a minimum from getDayWidth
    const minDayWidth = getDayWidth(zoomLevel);
    const responsiveDw = Math.max(minDayWidth, containerWidth / days.length);
    return { dateRange: { start, end, days }, totalWidth: days.length * responsiveDw, responsiveDayWidth: responsiveDw };
  }, [currentDate, zoomLevel, containerWidth]);

  const dayWidth = responsiveDayWidth;

  // ---- Space date ranges ----
  const spaceDateMap = useMemo(
    () => computeSpaceDateRanges(spaces, tasks),
    [spaces, tasks],
  );

  // ---- Group dates by month for header ----
  const groupedMonths = useMemo(() => {
    const months: { date: Date; dayCount: number }[] = [];
    let cur: Date | null = null;
    let count = 0;

    for (const day of dateRange.days) {
      if (!cur || !isSameMonth(day, cur)) {
        if (cur) months.push({ date: cur, dayCount: count });
        cur = day;
        count = 1;
      } else {
        count++;
      }
    }
    if (cur) months.push({ date: cur, dayCount: count });
    return months;
  }, [dateRange.days]);

  // ---- Navigation — move by the period amount ----
  const periodMonths = zoomLevel === 'month' ? 1 : zoomLevel === 'quarter' ? 3 : zoomLevel === 'semester' ? 6 : 12;
  const goToPrev = useCallback(() => {
    setCurrentDate((d) => subMonths(d, periodMonths));
  }, [periodMonths]);

  const goToNext = useCallback(() => {
    setCurrentDate((d) => addMonths(d, periodMonths));
  }, [periodMonths]);

  const goToToday = useCallback(() => {
    setCurrentDate(new Date());
  }, []);

  // ---- Scroll to today on mount / date change ----
  useEffect(() => {
    if (timelineRef.current) {
      const todayIdx = dateRange.days.findIndex((d) => isToday(d));
      if (todayIdx >= 0) {
        timelineRef.current.scrollLeft = Math.max(0, todayIdx * dayWidth - 200);
      }
    }
  }, [dateRange.days, dayWidth]);

  // ---- Sync vertical scroll between panels ----
  const handleLeftScroll = useCallback(() => {
    if (timelineRef.current && leftPanelRef.current) {
      timelineRef.current.scrollTop = leftPanelRef.current.scrollTop;
    }
  }, []);

  const handleTimelineScroll = useCallback(() => {
    if (leftPanelRef.current && timelineRef.current) {
      leftPanelRef.current.scrollTop = timelineRef.current.scrollTop;
    }
  }, []);

  // ---- Fetch real milestones from DB (single query for all spaces) ----
  const fetchMilestones = useCallback(async () => {
    if (spaces.length === 0) return;
    try {
      const spaceIds = spaces.map(s => s.id);
      const { data: rows } = await supabase
        .from('milestones')
        .select('*')
        .in('space_id', spaceIds)
        .order('target_date', { ascending: true, nullsFirst: false });

      if (!rows || rows.length === 0) { setMilestones([]); return; }

      const milestoneIds = rows.map(r => r.id);
      const { data: taskRows } = await supabase
        .from('tasks')
        .select('milestone_id, status')
        .in('milestone_id', milestoneIds);

      const progressMap = new Map<string, { total: number; completed: number }>();
      milestoneIds.forEach(id => progressMap.set(id, { total: 0, completed: 0 }));
      (taskRows || []).forEach(t => {
        const p = progressMap.get(t.milestone_id);
        if (p) { p.total++; if (isCompletedStatus(t.status)) p.completed++; }
      });

      const results: MilestoneWithProgress[] = rows.map(r => {
        const p = progressMap.get(r.id) || { total: 0, completed: 0 };
        return {
          id: r.id, spaceId: r.space_id, name: r.name,
          description: r.description || null, targetDate: r.target_date || null,
          status: (r.status as MilestoneStatus) || 'open', color: r.color || '#6366f1',
          orderIndex: r.order_index || 0, createdBy: r.created_by || null,
          createdAt: r.created_at, updatedAt: r.updated_at,
          totalTasks: p.total, completedTasks: p.completed,
          progressPercent: p.total > 0 ? Math.round((p.completed / p.total) * 100) : 0,
        };
      });

      // Auto-complete milestones at 100%
      for (const m of results) {
        if (m.progressPercent === 100 && m.status !== 'completed') {
          try { await completeMilestone(m.id); m.status = 'completed'; } catch { /* */ }
        }
      }
      setMilestones(results);
    } catch {
      // silently fail
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spaces.length]);

  useEffect(() => {
    fetchMilestones();
  }, [fetchMilestones]);

  const handleMilestonesChange = useCallback((updated: MilestoneWithProgress[]) => {
    setMilestones(updated);
  }, []);

  // ---- Tooltip handlers ----
  const handleBarMouseEnter = useCallback(
    (spaceId: string, e: React.MouseEvent) => {
      setHoveredSpaceId(spaceId);
      setTooltipInfo({ spaceId, x: e.clientX, y: e.clientY });
    },
    [],
  );

  const handleBarMouseMove = useCallback(
    (spaceId: string, e: React.MouseEvent) => {
      setTooltipInfo({ spaceId, x: e.clientX, y: e.clientY });
    },
    [],
  );

  const handleBarMouseLeave = useCallback(() => {
    setHoveredSpaceId(null);
    setTooltipInfo(null);
  }, []);

  // ---- Compute bar geometry for a space ----
  const getBarGeometry = useCallback(
    (spaceId: string) => {
      const range = spaceDateMap.get(spaceId);
      if (!range || range.earliest === Infinity) return null;

      const rangeStart = startOfDay(dateRange.days[0]);
      const startIdx = differenceInDays(
        startOfDay(new Date(range.earliest)),
        rangeStart,
      );
      const endIdx = differenceInDays(
        startOfDay(new Date(range.latest)),
        rangeStart,
      );

      const clampedStart = Math.max(0, startIdx);
      const clampedEnd = Math.min(dateRange.days.length - 1, endIdx);
      if (clampedEnd < 0 || clampedStart >= dateRange.days.length) return null;

      const left = clampedStart * dayWidth;
      const width = Math.max((clampedEnd - clampedStart + 1) * dayWidth, 24);

      return { left, width };
    },
    [spaceDateMap, dateRange.days, dayWidth],
  );

  // ---- Compute milestones geometry for a space (from real DB milestones) ----
  const getSpaceMilestones = useCallback(
    (spaceId: string): Array<{ x: number; milestone: MilestoneWithProgress }> => {
      const rangeStart = startOfDay(dateRange.days[0]);
      return milestones
        .filter((m) => m.spaceId === spaceId && m.targetDate)
        .map((m) => {
          const idx = differenceInDays(startOfDay(new Date(m.targetDate!)), rangeStart);
          if (idx < 0 || idx >= dateRange.days.length) return null;
          return { x: idx * dayWidth + dayWidth / 2, milestone: m };
        })
        .filter((item): item is { x: number; milestone: MilestoneWithProgress } => item !== null);
    },
    [milestones, dateRange.days, dayWidth],
  );

  // ---- Today line position ----
  const todayX = useMemo(() => {
    const idx = dateRange.days.findIndex((d) => isToday(d));
    if (idx < 0) return null;
    return idx * dayWidth + dayWidth / 2;
  }, [dateRange.days, dayWidth]);

  // ---- Tooltip data ----
  const tooltipSpace = useMemo(() => {
    if (!tooltipInfo) return null;
    const space = spaces.find((s) => s.id === tooltipInfo.spaceId);
    if (!space) return null;
    const range = spaceDateMap.get(space.id);
    return { space, range };
  }, [tooltipInfo, spaces, spaceDateMap]);

  // Filter to only show spaces that have bars or milestones overlapping the visible timeline
  const visibleSpaces = useMemo(() => {
    const rangeStartMs = dateRange.start.getTime();
    const rangeEndMs = dateRange.end.getTime();
    return spaces.filter(space => {
      // Check if bar overlaps
      const dr = spaceDateMap.get(space.id);
      if (dr && dr.earliest !== Infinity && dr.latest !== -Infinity) {
        if (dr.latest >= rangeStartMs && dr.earliest <= rangeEndMs) return true;
      }
      // Check if any milestone is in range
      const ms = milestones.filter(m => m.spaceId === space.id && m.targetDate);
      if (ms.some(m => m.targetDate! >= rangeStartMs && m.targetDate! <= rangeEndMs)) return true;
      return false;
    });
  }, [spaces, spaceDateMap, milestones, dateRange.start, dateRange.end]);

  const totalHeight = visibleSpaces.length * ROW_HEIGHT;

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-surface-primary">
      {/* Toolbar */}
      <div className="h-11 px-4 flex items-center gap-3 border-b border-border-primary bg-surface-secondary/50 shrink-0 overflow-visible relative z-20">
        {/* Nav buttons */}
        <div className="flex items-center gap-1">
          <button
            onClick={goToPrev}
            className="p-1 rounded hover:bg-surface-hover text-text-tertiary transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            onClick={goToToday}
            className="px-2 py-0.5 rounded text-[11px] font-medium text-text-secondary hover:bg-surface-hover transition-colors"
          >
            Oggi
          </button>
          <button
            onClick={goToNext}
            className="p-1 rounded hover:bg-surface-hover text-text-tertiary transition-colors"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        <span className="text-[12px] font-semibold text-text-primary capitalize">
          {zoomLevel === 'month'
            ? format(currentDate, 'MMMM yyyy', { locale: it })
            : `${format(dateRange.start, 'MMM yyyy', { locale: it })} – ${format(dateRange.end, 'MMM yyyy', { locale: it })}`
          }
        </span>

        <div className="flex-1" />

        {/* Milestones button */}
        <div className="relative" ref={milestoneBtnRef}>
          <button
            onClick={() => setMilestonesPanelOpen(!milestonesPanelOpen)}
            className={clsx(
              'p-1.5 rounded-md transition-colors',
              milestonesPanelOpen
                ? 'bg-accent-violet-dim text-accent-violet'
                : 'text-text-quaternary hover:bg-surface-hover hover:text-text-tertiary',
            )}
            title="Milestone"
          >
            <Diamond className="w-4 h-4" />
          </button>
          <MilestonesPanel
            isOpen={milestonesPanelOpen}
            onClose={() => setMilestonesPanelOpen(false)}
            spaces={spaces.map((s) => ({ id: s.id, name: s.name }))}
            onMilestonesChange={handleMilestonesChange}
            anchorRef={milestoneBtnRef}
            allTasks={tasks.map((t) => ({ id: t.id, name: t.name, spaceName: (t as unknown as { spaceName?: string }).spaceName || '', milestoneId: t.milestoneId }))}
          />
        </div>

        {/* Zoom toggle */}
        <div className="flex items-center bg-surface-tertiary/50 rounded-lg p-0.5">
          {(['month', 'quarter', 'semester', 'year'] as const).map((level) => (
            <button
              key={level}
              onClick={() => setZoomLevel(level)}
              className={clsx(
                'px-2 py-1 rounded-md text-[10px] font-semibold transition-all',
                zoomLevel === level
                  ? 'bg-surface-primary text-text-primary shadow-sm'
                  : 'text-text-quaternary hover:text-text-secondary',
              )}
            >
              {{ month: 'Mensile', quarter: 'Trimestre', semester: 'Semestre', year: 'Annuale' }[level]}
            </button>
          ))}
        </div>
      </div>

      {/* Main area */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left panel: project names */}
        <div
          className="shrink-0 border-r border-border-primary bg-surface-secondary flex flex-col"
          style={{ width: LEFT_PANEL_WIDTH }}
        >
          {/* Left header */}
          <div className="h-10 px-3 flex items-end pb-2 border-b border-border-primary bg-surface-tertiary/30 shrink-0">
            <span className="text-[10px] uppercase tracking-wider text-text-quaternary font-semibold">
              Progetto
            </span>
          </div>

          {/* Project list */}
          <div
            ref={leftPanelRef}
            className="flex-1 overflow-y-auto overflow-x-hidden"
            onScroll={handleLeftScroll}
          >
            <div style={{ height: totalHeight }}>
              {visibleSpaces.map((space, _i) => (
                <div
                  key={space.id}
                  className={clsx(
                    'flex items-center gap-2 px-3 border-b border-border-primary/30 cursor-pointer transition-colors',
                    hoveredSpaceId === space.id
                      ? 'bg-surface-hover'
                      : 'hover:bg-surface-hover/50',
                  )}
                  style={{ height: ROW_HEIGHT }}
                  onClick={() => onSpaceNavigate(space.id)}
                  onMouseEnter={() => setHoveredSpaceId(space.id)}
                  onMouseLeave={() => setHoveredSpaceId(null)}
                >
                  <span className="text-[11px] text-text-primary font-medium truncate flex-1">
                    {space.name}
                  </span>
                  <span
                    className={clsx(
                      'text-[10px] font-semibold px-1.5 py-0.5 rounded-full shrink-0',
                      space.completionRate >= 80
                        ? 'bg-accent-emerald/15 text-accent-emerald'
                        : space.completionRate >= 40
                          ? 'bg-accent-amber/15 text-accent-amber'
                          : 'bg-accent-violet/15 text-accent-violet',
                    )}
                  >
                    {space.completionRate}%
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right panel: timeline */}
        <div
          ref={timelineRef}
          className="flex-1 overflow-auto"
          onScroll={handleTimelineScroll}
        >
          <div style={{ width: totalWidth, minHeight: '100%' }} className="flex flex-col">
            {/* Timeline header */}
            <div className="h-10 border-b border-border-primary bg-surface-secondary/30 sticky top-0 z-10 shrink-0 flex flex-col">
              {/* Month labels */}
              <div className="h-5 flex">
                {groupedMonths.map(({ date, dayCount }) => (
                  <div
                    key={format(date, 'yyyy-MM')}
                    className="flex items-center justify-center text-[10px] font-semibold text-text-secondary capitalize border-r border-border-primary/30"
                    style={{ width: dayCount * dayWidth }}
                  >
                    {dayCount * dayWidth > 50
                      ? format(date, 'MMM yyyy', { locale: it })
                      : format(date, 'MMM', { locale: it })}
                  </div>
                ))}
              </div>
              {/* Week/day tick marks */}
              <div className="h-5 flex">
                {dateRange.days.map((day, i) => {
                  const isMonday = day.getDay() === 1;
                  const today = isToday(day);
                  return (
                    <div
                      key={i}
                      className={clsx(
                        'flex items-center justify-center border-r border-border-primary/15',
                        today && 'bg-accent-violet/10',
                      )}
                      style={{ width: dayWidth }}
                    >
                      {zoomLevel === 'month' && isMonday && dayWidth >= 6 && (
                        <span className="text-[8px] text-text-quaternary tabular-nums">
                          {format(day, 'd')}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Chart body */}
            <div className="relative" style={{ height: totalHeight }}>
              {/* Grid lines */}
              <div className="absolute inset-0 flex pointer-events-none">
                {dateRange.days.map((day, i) => (
                  <div
                    key={i}
                    className={clsx(
                      'h-full border-r border-border-primary/10',
                      day.getDay() === 0 || day.getDay() === 6
                        ? 'bg-surface-tertiary/8'
                        : '',
                    )}
                    style={{ width: dayWidth }}
                  />
                ))}
              </div>

              {/* Row separators */}
              {spaces.map((_, i) => (
                <div
                  key={i}
                  className="absolute left-0 right-0 border-b border-border-primary/15"
                  style={{ top: (i + 1) * ROW_HEIGHT }}
                />
              ))}

              {/* Today line */}
              {todayX !== null && (
                <div
                  className="absolute top-0 bottom-0 w-px z-20"
                  style={{
                    left: todayX,
                    background: 'rgb(var(--accent-rose))',
                    boxShadow: '0 0 8px rgba(244,63,94,0.3)',
                  }}
                >
                  <div
                    className="absolute -top-0 -left-[3px] w-[7px] h-[7px] rounded-full"
                    style={{ background: 'rgb(var(--accent-rose))' }}
                  />
                </div>
              )}

              {/* Bars and milestones */}
              {visibleSpaces.map((space, rowIdx) => {
                const bar = getBarGeometry(space.id);
                const spaceMilestones = getSpaceMilestones(space.id);
                const isHovered = hoveredSpaceId === space.id;

                return (
                  <div key={space.id} className="absolute left-0" style={{ top: 0, height: ROW_HEIGHT, width: '100%', transform: `translateY(${rowIdx * ROW_HEIGHT}px)` }}>
                    {/* Bar */}
                    {bar && (
                      <div
                        className={clsx(
                          'absolute rounded-md cursor-pointer transition-all overflow-hidden',
                          isHovered && 'shadow-lg brightness-110 z-10',
                        )}
                        style={{
                          left: bar.left,
                          width: bar.width,
                          top: (ROW_HEIGHT - BAR_HEIGHT) / 2,
                          height: BAR_HEIGHT,
                        }}
                        onClick={() => onSpaceNavigate(space.id)}
                        onMouseEnter={(e) => handleBarMouseEnter(space.id, e)}
                        onMouseMove={(e) => handleBarMouseMove(space.id, e)}
                        onMouseLeave={handleBarMouseLeave}
                      >
                        {/* Background track */}
                        <div className="absolute inset-0 rounded-md bg-violet-500/30 border border-violet-400/40" />
                        {/* Progress fill */}
                        <div
                          className="absolute inset-y-0 left-0 rounded-md transition-all"
                          style={{
                            width: `${Math.max(space.completionRate, 2)}%`,
                            background:
                              space.completionRate >= 80
                                ? '#22c55e'
                                : space.completionRate >= 40
                                  ? '#f59e0b'
                                  : '#8b5cf6',
                            opacity: 0.85,
                          }}
                        />
                        {/* Label inside or next to bar */}
                        <span className={clsx(
                          'relative z-10 text-[10px] font-semibold truncate leading-[20px] whitespace-nowrap',
                          bar.width > 60 ? 'px-2 text-white' : 'pl-1 text-text-secondary',
                        )} style={bar.width <= 60 ? { marginLeft: bar.width + 4 } : undefined}>
                          {space.name}
                        </span>
                      </div>
                    )}

                    {/* Milestones (diamonds) from DB */}
                    {spaceMilestones.map(({ x: mx, milestone: m }) => (
                      <div
                        key={m.id}
                        className={clsx(
                          'absolute rotate-45 rounded-sm cursor-pointer z-10',
                          isHovered && 'brightness-110',
                        )}
                        style={{
                          left: mx - DIAMOND_SIZE / 2,
                          top: (ROW_HEIGHT - DIAMOND_SIZE) / 2,
                          width: DIAMOND_SIZE,
                          height: DIAMOND_SIZE,
                          backgroundColor: m.color || '#f59e0b',
                        }}
                        onMouseEnter={(e) => setMilestoneTooltip({ milestone: m, x: e.clientX, y: e.clientY })}
                        onMouseMove={(e) => setMilestoneTooltip({ milestone: m, x: e.clientX, y: e.clientY })}
                        onMouseLeave={() => setMilestoneTooltip(null)}
                      />
                    ))}
                  </div>
                );
              })}

              {/* Empty state */}
              {visibleSpaces.length === 0 && (
                <div className="flex items-center justify-center h-64 text-text-quaternary">
                  <div className="text-center">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-surface-tertiary flex items-center justify-center">
                      <Calendar className="w-8 h-8 text-text-quaternary" />
                    </div>
                    <p className="text-[13px] font-medium text-text-secondary">
                      Nessun progetto
                    </p>
                    <p className="text-[11px] text-text-quaternary mt-1">
                      I progetti appariranno qui quando avranno task con date
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Floating tooltip — bar */}
      {tooltipInfo && tooltipSpace && (
        <div
          className="fixed z-50 pointer-events-none bg-surface-primary border border-border-primary rounded-lg shadow-xl px-3 py-2 min-w-[180px]"
          style={{
            left: tooltipInfo.x + 12,
            top: tooltipInfo.y - 10,
          }}
        >
          <div className="text-[11px] font-semibold text-text-primary mb-1">
            {tooltipSpace.space.name}
          </div>
          {tooltipSpace.range && tooltipSpace.range.earliest !== Infinity && (
            <div className="text-[10px] text-text-secondary mb-1">
              {format(new Date(tooltipSpace.range.earliest), 'd MMM yyyy', {
                locale: it,
              })}{' '}
              &mdash;{' '}
              {format(new Date(tooltipSpace.range.latest), 'd MMM yyyy', {
                locale: it,
              })}
            </div>
          )}
          <div className="flex items-center gap-3 text-[10px] text-text-tertiary">
            <span>{tooltipSpace.space.taskCount} task</span>
            <span>{tooltipSpace.space.completionRate}%</span>
            {tooltipSpace.space.overdueTasks > 0 && (
              <span className="text-accent-rose">
                {tooltipSpace.space.overdueTasks} in ritardo
              </span>
            )}
          </div>
        </div>
      )}

      {/* Floating tooltip — milestone */}
      {milestoneTooltip && (
        <div
          className="fixed z-50 pointer-events-none bg-surface-primary border border-border-primary rounded-lg shadow-xl px-3 py-2 min-w-[160px]"
          style={{
            left: milestoneTooltip.x + 12,
            top: milestoneTooltip.y - 10,
          }}
        >
          <div className="flex items-center gap-1.5 mb-1">
            <Diamond className="w-3 h-3" style={{ color: milestoneTooltip.milestone.color }} />
            <span className="text-[11px] font-semibold text-text-primary">
              {milestoneTooltip.milestone.name}
            </span>
          </div>
          {milestoneTooltip.milestone.targetDate && (
            <div className="text-[10px] text-text-secondary mb-1">
              {format(new Date(milestoneTooltip.milestone.targetDate), 'd MMM yyyy', { locale: it })}
            </div>
          )}
          <div className="flex items-center gap-2 text-[10px] text-text-tertiary">
            <span>
              {milestoneTooltip.milestone.completedTasks}/{milestoneTooltip.milestone.totalTasks} task
            </span>
            <span>{milestoneTooltip.milestone.progressPercent}%</span>
          </div>
        </div>
      )}
    </div>
  );
}
